import EventDelegation from '@jjwesterkamp/event-delegation'
import { gsap } from 'gsap'
import DOMTarget = gsap.DOMTarget
import { addClasses, deferFrames } from 'lambda-dom'

import { ScrollBlockers } from '@app-helpers'
import { current } from '@app-services'
import { EmployeeCard } from './our-structure.types'
import {
    findActiveDropdown,
    findActiveTextContainer,
    findCardTemplate,
    findDropdownContainer,
    findDropDowns,
    findGrid,
    findGridContainer,
    findIntermezzo,
    findIntermezzoCanvas,
    findTabs,
    findTabSlider,
    findTexts,
} from './our-structure.bootstrap'
import { fetchEmployeeGrid, fetchEmployeeIntermezzo, fetchFirstTabIdForEmployee } from './our-structure.store'

// ----------------------
// Animation Handling
// ----------------------

const activateGsapAnimations = (cards: NodeListOf<Element>): void => {
    const gsapCards = gsap.utils.toArray(cards) as DOMTarget[]

    gsapCards.forEach((card: DOMTarget) => {
        gsap.to(card, {
            y: -150,
            ease: 'power1.inOut',
            duration: 1,
            opacity: 1,
            onComplete() {
                (card as HTMLElement).style.transition = '250ms ease-in-out'
            },
        })
    })
}

// ----------------------
// Intermezzo Handling
// ----------------------

const activateOtherEmployeeListeners = (canvas: HTMLElement, tabId: string): void => {
    const otherEmployees = canvas.querySelectorAll('.osi__other')

    otherEmployees.forEach((otherEmployee) => {
        if (! (otherEmployee instanceof HTMLElement) || ! otherEmployee.dataset.id) return

        otherEmployee.addEventListener('click', () => {
            refreshEmployeeIntermezzo(otherEmployee.dataset.id!, canvas, tabId)
        })
    })
}

const handleEmployeeIntermezzo = (id: string, tabId: string) => {
    const intermezzoContainer = findIntermezzo()

    if (! intermezzoContainer) return

    const canvas = findIntermezzoCanvas(intermezzoContainer)

    if (! canvas) return

    fetchEmployeeIntermezzo(id, tabId).then((intermezzo) => {
        ScrollBlockers.add('article-preview')

        canvas.innerHTML = intermezzo

        activateOtherEmployeeListeners(canvas, tabId)

        deferFrames(1, () => addClasses('active')(intermezzoContainer))
    })
}

const refreshEmployeeIntermezzo = (id: string, canvas: HTMLElement, tabId: string): void => {
    canvas.querySelector('.osi__content')?.classList.add('hide')

    deferFrames(15, () => fetchEmployeeIntermezzo(id, tabId).then((intermezzo) => {
        canvas.innerHTML = intermezzo

        activateOtherEmployeeListeners(canvas, tabId)
    }))
}

// ----------------------
// Grid Handling
// ----------------------

const generateNewEmployeeCard = (employee: EmployeeCard, template: HTMLElement, tabId: string): HTMLElement => {
    template.classList.remove('our-structure__card-template')

    template.dataset.id = employee.id
    template.dataset.tabId = tabId

    const image = template.querySelector('.our-structure__card__image')

    if (image && image instanceof HTMLImageElement) {
        image.src = employee.avatarUrl
        image.alt = `portrait for ${employee.fullName}`
    }

    const name = template.querySelector('.our-structure__card__name')

    if (name && name instanceof HTMLElement) {
        name.innerText = employee.fullName
    }

    const position = template.querySelector('.our-structure__card__position')

    if (position && position instanceof HTMLElement && employee.position) {
        position.innerText = employee.position
    }

    if (position && position instanceof HTMLElement && ! employee.position) {
        position.style.display = 'none'
    }

    const secondaryPosition = template.querySelector('.our-structure__card__position-animated')

    if (secondaryPosition && secondaryPosition instanceof HTMLElement && employee.position) {
        secondaryPosition.innerText = employee.position
    }

    const secondaryPositionContainer = template.querySelector('.our-structure__card__position-animated__container')

    if (secondaryPositionContainer && secondaryPositionContainer instanceof HTMLElement && ! employee.position) {
        secondaryPositionContainer.style.display = 'none'
    }

    return template
}

const setOrUnsetFlexOnGridContainer = (set: boolean = true): void => {
    const container = findGridContainer()

    if (! container) return

    if (set) container.classList.add('flex')

    if (! set) container.classList.remove('flex')
}

const placeEmployeesInGrid = (employees: EmployeeCard[], tabId: string): void => {
    const grid = findGrid()
    const template = findCardTemplate()

    if (! grid || ! template) return

    setOrUnsetFlexOnGridContainer(employees.length <= 3)

    const gridColumnCount = employees.length <= 4 ? employees.length : 4

    grid.style.setProperty('--grid-column-count', `${gridColumnCount}`)

    grid.replaceChildren()

    const newChildren = employees.map((employee) => {
        return generateNewEmployeeCard(employee, template.cloneNode(true) as HTMLElement, tabId)
    })

    grid.replaceChildren(...newChildren)

    const cards = grid.querySelectorAll('.our-structure__card')

    cards.forEach((card) => {
        card.addEventListener('click', () => {
            if (! (card instanceof HTMLElement) || ! card.dataset.id) return

            handleEmployeeIntermezzo(card.dataset.id, tabId)
        })
    })

    activateGsapAnimations(cards)
}

// ----------------------
// Selection Handling
// ----------------------

const handleTabSlider = (tab: Element, slider: HTMLElement | null): void => {
    if (! slider || ! (tab instanceof HTMLElement)) return

    const textElement = tab.querySelector('.our-structure__tab-text')

    if (! textElement || ! (textElement instanceof HTMLElement)) return

    slider.style.setProperty('--x-position', `${tab.offsetLeft.toString()}px`)

    slider.style.setProperty('--x-width', `${textElement.offsetWidth.toString()}px`)
}

const activateCorrectDropDownText = (activeDropDown: Element, dropdowns: NodeListOf<Element>, id: string) => {
    const activeTextContainer = findActiveTextContainer(activeDropDown)

    if (! activeTextContainer) return

    let value: string = ''

    dropdowns.forEach((dropdown) => {
        if (! (dropdown instanceof HTMLElement)) return

        if (dropdown.dataset.id && dropdown.dataset.id === id) {
            value = dropdown.innerText
        }
    })

    if (value) activeTextContainer.innerText = value
}

const handleSelectionElementClick = (
    texts: NodeListOf<Element>,
    tabs: NodeListOf<Element>,
    dropdowns: NodeListOf<Element>,
    element: Element,
    activeDropDown: Element | undefined,
): void => {
    if (! (element instanceof HTMLElement)) return

    const id = element.dataset.id

    if (! id) return

    activateAppropriateElement(tabs, id)
    activateAppropriateElement(dropdowns, id)
    activateAppropriateElement(texts, id)

    if (activeDropDown) activateCorrectDropDownText(activeDropDown, dropdowns, id)

    fetchEmployeeGrid(id).then((employees) => {
        if (employees) {
            placeEmployeesInGrid(employees, id)
        }
    })
}

const activateAppropriateElement = (elements: NodeListOf<Element>, id: string): void => {
    elements.forEach((element) => {
        if (! (element instanceof HTMLElement)) return

        if (element.dataset.id && element.dataset.id === id) {
            element.classList.add('active')

            return
        }

        element.classList.remove('active')
    })
}

const handleDropDownContainer = (activeDropDown: Element, dropDownContainer: Element) => {
    activeDropDown.addEventListener('click', () => {
        activeDropDown.classList.toggle('active')
        dropDownContainer.classList.toggle('active')
    })
}

const removeDropDownContainer = (activeDropDown: Element, dropDownContainer: Element) => {
    activeDropDown.classList.remove('active')
    dropDownContainer.classList.remove('active')
}

// ----------------------
// Initiator
// ----------------------

export const initiateOurStructureSelection = (): void => {
    const tabs = findTabs()
    const dropdowns = findDropDowns()
    const texts = findTexts()

    const activeDropDown = findActiveDropdown()
    const dropDownContainer = findDropdownContainer()

    const slider = findTabSlider()

    if (! activeDropDown || ! dropDownContainer) return

    tabs.forEach((tab: Element) => {
        tab.addEventListener('click', () => {
            handleSelectionElementClick(texts, tabs, dropdowns, tab, activeDropDown)
            handleTabSlider(tab, slider)
        })
    })

    dropdowns.forEach((dropdown: Element) => {
        dropdown.addEventListener('click', () => {
            handleSelectionElementClick(texts, tabs, dropdowns, dropdown, activeDropDown)
            removeDropDownContainer(activeDropDown, dropDownContainer)
        })
    })

    handleDropDownContainer(activeDropDown, dropDownContainer)

    if (tabs[0] instanceof HTMLElement && tabs[0].dataset.id) {
        const id = tabs[0].dataset.id

        fetchEmployeeGrid(id)
            .then((employees) => {
                if (employees) {
                    placeEmployeesInGrid(employees, id)
                }
            })

        handleTabSlider(tabs[0], slider)
    }

    const queryObject = current.queryObject()

    if (queryObject.employee
        && typeof queryObject.employee === 'string'
        && tabs[0] instanceof HTMLElement
        && tabs[0].dataset.id
    ) {
        const employeeId = queryObject.employee

        fetchFirstTabIdForEmployee(employeeId).then((tabId) => {
            handleEmployeeIntermezzo(employeeId, tabId)
        })
    }

    EventDelegation
        .global()
        .events('click')
        .select('[data-clear-preview]')
        .listen(() => {
            ScrollBlockers.remove('article-preview')

            const intermezzoContainer = findIntermezzo()

            if (! intermezzoContainer) return

            const canvas = findIntermezzoCanvas(intermezzoContainer)

            if (! canvas) return

            intermezzoContainer.classList.remove('active')

            setTimeout(() => {
                canvas.innerHTML = ''
            }, 200)
        })
}
